import { Calculator, Info, RotateCcw } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale, intlLocale } from '@/lib/i18n';
import { formatMinor, usePublicPlanCatalog } from '@/lib/plan-catalog';
import { rich } from './legal-pages';
import {
  calculateRoi,
  DEFAULT_ASSUMPTIONS,
  DEFAULT_INPUTS,
  DEFAULT_TEAM,
  netOfSubscription,
  pickPlan,
  seatsNeeded,
  type RoiAssumptions,
  type RoiInputs,
  type TeamSize,
} from './roi';

/* ---------------------------------------------------------------------------
   מחשבון ההחזר בדף המכירה. החישוב ב-roi.ts (טהור, עם טסט); כאן רק הטופס.
   הערכים נשמרים כמחרוזות — כדי שאפשר יהיה למחוק שדה ולהקליד מחדש בלי
   שיקפוץ ל-0. שדה ריק נחשב 0.
   עלות המנוי: לפי גודל הצוות ומשקלי המושבים — התוכנית הזולה שמתאימה, מהקטלוג.
   --------------------------------------------------------------------------- */

type Key = keyof RoiInputs | keyof RoiAssumptions | keyof TeamSize;
type Values = Record<Key, string>;

const toStrings = (): Values => {
  const all = { ...DEFAULT_INPUTS, ...DEFAULT_ASSUMPTIONS, ...DEFAULT_TEAM };
  return Object.fromEntries(Object.entries(all).map(([k, v]) => [k, String(v)])) as Values;
};

const num = (s: string) => {
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

interface FieldDef {
  key: Key;
  labelKey:
    | 'requests'
    | 'minutes'
    | 'hourly'
    | 'jobValue'
    | 'quotes'
    | 'closeRate'
    | 'reduction'
    | 'lift'
    | 'office'
    | 'field';
  hintKey?: 'minutesHint';
  max?: number;
  step?: number;
}

const INPUT_FIELDS: FieldDef[] = [
  { key: 'requestsPerMonth', labelKey: 'requests' },
  { key: 'adminMinutesPerRequest', labelKey: 'minutes', hintKey: 'minutesHint' },
  { key: 'hourlyCost', labelKey: 'hourly', step: 5 },
  { key: 'avgJobValue', labelKey: 'jobValue', step: 50 },
  { key: 'quotesPerMonth', labelKey: 'quotes' },
  { key: 'closeRatePct', labelKey: 'closeRate', max: 100 },
];
const TEAM_FIELDS: FieldDef[] = [
  { key: 'officeUsers', labelKey: 'office' },
  { key: 'fieldUsers', labelKey: 'field' },
];
const ASSUMPTION_FIELDS: FieldDef[] = [
  { key: 'adminReductionPct', labelKey: 'reduction', max: 100, step: 5 },
  { key: 'closeRateLiftPts', labelKey: 'lift', max: 100 },
];

export function RoiCalculator() {
  const { t } = useTranslation('public');
  const id = useId();
  const [values, setValues] = useState<Values>(toStrings);

  const n = useMemo(() => {
    const out = {} as Record<Key, number>;
    for (const k of Object.keys(values) as Key[]) out[k] = num(values[k]);
    return out;
  }, [values]);
  const r = calculateRoi(n, n);
  const catalog = usePublicPlanCatalog();
  const seats = seatsNeeded(n, catalog);
  const pick = pickPlan(catalog, seats);
  const net = pick ? netOfSubscription(r, pick.monthlyMinor / 100) : null;

  const locale = intlLocale();
  const ils = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'ILS',
    maximumFractionDigits: 0,
  });
  const dec = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const int = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });

  const field = (f: FieldDef, tone: 'plain' | 'assumption') => {
    const inputId = `${id}-${f.key}`;
    const hintId = f.hintKey ? `${inputId}-hint` : undefined;
    return (
      <div key={f.key} className="space-y-1">
        <label htmlFor={inputId} className="block text-sm font-medium text-fg">
          {t(`landing.calc.${f.labelKey}`)}
        </label>
        <input
          id={inputId}
          type="number"
          inputMode="decimal"
          min={0}
          max={f.max}
          step={f.step ?? 1}
          value={values[f.key]}
          aria-describedby={hintId}
          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          className={`tabular h-11 w-full rounded-(--radius-md) border px-3 text-base text-fg shadow-xs transition-colors focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring ${
            tone === 'assumption'
              ? 'border-violet-border bg-surface'
              : 'border-border-strong bg-surface'
          }`}
        />
        {f.hintKey ? (
          <p id={hintId} className="text-xs text-fg-subtle">
            {t(`landing.calc.${f.hintKey}`)}
          </p>
        ) : null}
      </div>
    );
  };

  const lift = Math.min(
    Math.max(n.closeRateLiftPts, 0),
    100 - Math.min(Math.max(n.closeRatePct, 0), 100),
  );

  return (
    <div className="overflow-hidden rounded-(--radius-2xl) border border-border bg-surface-raised shadow-lg">
      <span className="bg-brand-gradient block h-1" aria-hidden />
      <div className="space-y-2 border-b border-border px-5 py-5 sm:px-8">
        <h3 className="flex flex-wrap items-center gap-2 text-xl font-semibold text-fg">
          <Calculator className="size-5 text-accent" aria-hidden />
          {t('landing.calc.title')}
          <span className="rounded-full bg-warning-subtle px-2.5 py-0.5 text-xs font-semibold text-warning ring-1 ring-inset ring-warning-border/60">
            {t('landing.calc.badge')}
          </span>
        </h3>
        <p className="text-sm text-fg-muted">{t('landing.calc.subtitle')}</p>
      </div>

      <div className="grid lg:grid-cols-5">
        {/* הקלטים וההנחות */}
        <form
          className="space-y-6 px-5 py-6 sm:px-8 lg:col-span-3"
          onSubmit={(e) => e.preventDefault()}
        >
          <fieldset className="space-y-4">
            <legend className="mb-3 text-sm font-semibold ltr:uppercase ltr:tracking-wide text-fg-muted">
              {t('landing.calc.inputsTitle')}
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              {INPUT_FIELDS.map((f) => field(f, 'plain'))}
            </div>
          </fieldset>
          <fieldset className="space-y-3">
            <legend className="mb-3 text-sm font-semibold ltr:uppercase ltr:tracking-wide text-fg-muted">
              {t('landing.calc.teamTitle')}
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              {TEAM_FIELDS.map((f) => field(f, 'plain'))}
            </div>
            <p className="text-xs text-fg-subtle">{t('landing.calc.teamHint')}</p>
          </fieldset>
          <fieldset className="space-y-3 rounded-(--radius-lg) border border-violet-border bg-violet-subtle p-4">
            <legend className="px-1 text-sm font-semibold text-violet">
              {t('landing.calc.assumptionsTitle')}
            </legend>
            <p className="flex gap-2 text-xs leading-relaxed text-fg-muted">
              <Info className="mt-0.5 size-3.5 shrink-0 text-violet" aria-hidden />
              {t('landing.calc.assumptionsBody')}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {ASSUMPTION_FIELDS.map((f) => field(f, 'assumption'))}
            </div>
          </fieldset>
          <button
            type="button"
            onClick={() => setValues(toStrings())}
            className="inline-flex items-center gap-1.5 rounded-(--radius-md) text-sm font-medium text-accent hover:underline"
          >
            <RotateCcw className="size-3.5" aria-hidden />
            {t('landing.calc.reset')}
          </button>
        </form>

        {/* התוצאות */}
        <section
          aria-labelledby={`${id}-results`}
          className="space-y-4 border-t border-border bg-nav px-5 py-6 text-nav-fg sm:px-8 lg:col-span-2 lg:border-s lg:border-t-0"
        >
          <h4
            id={`${id}-results`}
            className="text-sm font-semibold ltr:uppercase ltr:tracking-wide text-nav-fg-muted"
          >
            {t('landing.calc.resultsTitle')}
          </h4>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm text-nav-fg-muted">{t('landing.calc.hours')}</dt>
              <dd className="tabular text-2xl font-bold">
                {t('landing.calc.hoursValue', { hours: dec.format(r.hoursSavedPerMonth) })}
              </dd>
              <dd className="text-xs text-nav-fg-muted">
                {t('landing.calc.hoursHow', {
                  requests: int.format(n.requestsPerMonth),
                  minutes: dec.format(n.adminMinutesPerRequest),
                  reduction: dec.format(n.adminReductionPct),
                })}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-nav-fg-muted">{t('landing.calc.saved')}</dt>
              <dd className="tabular text-2xl font-bold">{ils.format(r.moneySavedPerMonth)}</dd>
              <dd className="text-xs text-nav-fg-muted">
                {t('landing.calc.savedHow', {
                  hours: dec.format(r.hoursSavedPerMonth),
                  rate: ils.format(n.hourlyCost),
                })}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-nav-fg-muted">{t('landing.calc.extra')}</dt>
              <dd className="tabular text-2xl font-bold">{ils.format(r.extraRevenuePerMonth)}</dd>
              <dd className="text-xs text-nav-fg-muted">
                {t('landing.calc.extraHow', {
                  quotes: int.format(n.quotesPerMonth),
                  lift: dec.format(lift),
                  value: ils.format(n.avgJobValue),
                })}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-nav-fg-muted">{t('landing.calc.plan')}</dt>
              {pick ? (
                <>
                  <dd className="tabular text-2xl font-bold">
                    {t(
                      catalog.vatRateBps > 0
                        ? 'landing.calc.planValue'
                        : 'landing.calc.planValueNoVat',
                      {
                        price: formatMinor(pick.monthlyMinor, currentLocale()),
                      },
                    )}
                  </dd>
                  <dd className="text-xs text-nav-fg-muted">
                    {pick.extraSeats > 0
                      ? t('landing.calc.planHowExtra', {
                          plan: t(`landing.pricing.plans.${pick.plan}.name`),
                          seats: int.format(Math.max(seats, 1)),
                          extra: int.format(pick.extraSeats),
                        })
                      : t('landing.calc.planHow', {
                          plan: t(`landing.pricing.plans.${pick.plan}.name`),
                          seats: int.format(Math.max(seats, 1)),
                        })}
                  </dd>
                </>
              ) : (
                <dd className="text-sm text-nav-fg [&_a]:text-accent-bright">
                  {rich(t('landing.calc.planContact', { max: catalog.maxTotalSeats }))}
                </dd>
              )}
            </div>
            <div
              className="rounded-(--radius-lg) bg-white/10 p-4 ring-1 ring-white/15"
              aria-live="polite"
              aria-atomic="true"
            >
              <dt className="text-sm font-medium text-nav-fg">{t('landing.calc.yearly')}</dt>
              <dd className="tabular text-3xl font-bold text-accent-bright">
                {ils.format(r.yearlyImpact)}
              </dd>
              <dd className="text-xs text-nav-fg-muted">{t('landing.calc.yearlyHow')}</dd>
              {net ? (
                <dd className="tabular mt-2 text-sm font-medium text-nav-fg">
                  {t('landing.calc.net', { amount: ils.format(net.netYearly) })}
                </dd>
              ) : null}
            </div>
          </dl>
          <p className="text-xs leading-relaxed text-nav-fg-muted">
            {t('landing.calc.disclaimer')}
          </p>
        </section>
      </div>
    </div>
  );
}
